import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";

const LEVELS = [
  { k: "Easy", icon: Icons.Smile, tone: "emerald", desc: "Accepts small, quick blinks. Best for low light, glasses or small eyes. Photos are still rejected." },
  { k: "Normal", icon: Icons.Eye, tone: "blue", desc: "A clear, normal blink is required. Balanced for most cameras and offices." },
  { k: "Strict", icon: Icons.ShieldCheck, tone: "rose", desc: "A full, deliberate blink with eyes fully reopened. Highest anti-spoofing, may need a few tries." },
];
const TONES = { emerald: "border-emerald-400 bg-emerald-50 text-emerald-700", blue: "border-blue-400 bg-blue-50 text-blue-700", rose: "border-rose-400 bg-rose-50 text-rose-700" };

const ClockAccess = () => {
  const [levels, setLevels] = useState([]);
  const [draft, setDraft] = useState({});
  const load = () => api.get("/attendance/clock-access").then(({ data }) => { setLevels(data.levels); setDraft(Object.fromEntries(data.levels.map((l) => [l.level, l.allowed]))); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const dirty = levels.some((l) => draft[l.level] !== l.allowed);
  const save = async () => {
    try { await api.put("/attendance/clock-access", { allowed_levels: Object.keys(draft).filter((k) => draft[k]) }); toast.success("Clock In/Out access saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 max-w-4xl mb-4" data-testid="clock-access">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div><h3 className="text-sm font-semibold text-slate-900">Clock In/Out Access by Employee Level</h3>
          <p className="text-xs text-slate-500">Ticked levels can use their own Clock In/Out (selfie + location, then the last 5 digits of their Biometric ID). Rank &amp; File, OJT and Probationary always use the Shared Clock In/Out. Roles with "Personal Clock In/Out" in Roles &amp; Permissions can also use their own.</p></div>
        <Button size="sm" onClick={save} disabled={!dirty} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="clock-access-save">Save</Button>
      </div>
      <div className="flex flex-wrap gap-2">{levels.map((l) => l.kiosk_only ? (
        <span key={l.level} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-400" title="Uses the Shared Clock In/Out" data-testid={`clock-access-${l.level}`}>
          <Icons.Lock className="h-3.5 w-3.5" />{l.level} · Shared Clock only
        </span>) : (
        <button key={l.level} type="button" onClick={() => setDraft({ ...draft, [l.level]: !draft[l.level] })}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${draft[l.level] ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`} data-testid={`clock-access-${l.level}`}>
          {draft[l.level] ? <Icons.CheckSquare className="h-3.5 w-3.5" /> : <Icons.Square className="h-3.5 w-3.5" />}{l.level}
        </button>))}</div>
    </div>
  );
};

export default function ClockSettings() {
  const [saved, setSaved] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/hris/config/clock-settings").then(({ data }) => { setSaved(data.blink_strictness); setSel(data.blink_strictness); }); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/hris/config/clock-settings", { blink_strictness: sel }); setSaved(sel); toast.success(`Blink checking set to ${sel}`); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  return (
    <div className="uemp-fade-in">
      <PageHeader title="Clock In/Out Settings" subtitle="Who can use Clock In/Out, and how strict the blink (liveness) check is" />
      <ClockAccess />
      <div className="bg-white rounded-2xl border border-slate-200 p-5 max-w-4xl" data-testid="clock-settings">
        <h3 className="text-sm font-semibold text-slate-900">Blink Strictness</h3>
        <p className="text-xs text-slate-500 mb-4">Applies to every employee's face scan on the next Clock In/Out.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {LEVELS.map(({ k, icon: I, tone, desc }) => (
            <button key={k} type="button" onClick={() => setSel(k)} className={`text-left rounded-xl border-2 p-4 transition-colors ${sel === k ? TONES[tone] : "border-slate-200 hover:border-slate-300 text-slate-700"}`} data-testid={`blink-${k.toLowerCase()}`}>
              <div className="flex items-center gap-2"><I className="h-5 w-5" /><span className="font-semibold">{k}</span>{saved === k && <span className="ml-auto rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200">Current</span>}</div>
              <p className="mt-2 text-xs text-slate-600">{desc}</p>
            </button>
          ))}
        </div>
        <div className="mt-5 flex justify-end"><Button onClick={save} disabled={!sel || sel === saved || busy} className="bg-blue-600 hover:bg-blue-700" data-testid="clock-settings-save">Save</Button></div>
      </div>
    </div>
  );
}
